import type { CallOffPublicView } from '../types';

import {
  type QueryFunctionContext,
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { HTTPError } from 'ky';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysCallOff } from '../queryKeys';

import { fetchCallOffViaMagicLink } from './fetchCallOffViaMagicLink';

/**
 * Generates the query options for the magic link call-off query.
 *
 * @param callOffId - The ID of the call-off to fetch.
 * @param token - The magic link token for authorization.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getCallOffViaMagicLinkQueryOptions = <TData = CallOffPublicView>({
  callOffId,
  token,
  options,
}: {
  callOffId: UUID | undefined;
  token: string | undefined;
  options?: Omit<
    UseQueryOptions<CallOffPublicView, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn:
      callOffId && token
        ? async ({ signal }: QueryFunctionContext) =>
            fetchCallOffViaMagicLink(callOffId, token, { signal })
        : skipToken,
    queryKey: queryKeysCallOff.getViaMagicLink(callOffId ?? '', token ?? ''),
    retry: (failureCount, error) => {
      if (error instanceof HTTPError && error.response.status === 401) {
        // It's pointless to retry if the magic link token is invalid
        return false;
      }

      return failureCount <= 5;
    },
    staleTime: ms('60s'),
    ...options,
  });

/**
 * Custom React Query hook to fetch public call-off data via magic link.
 *
 * @param params - An object containing:
 *   - callOffId: The ID of the call-off to fetch.
 *   - token: The magic link token for authorization.
 * @param options - (Optional) Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 * @see https://add.dev.vestigas.com/redoc#tag/Call-Off-Magic-Link-(Public)/operation/get_calloff_via_magic_link_calloffs_magic_link__calloff_id__get
 */
export const useQueryCallOffViaMagicLink = (
  {
    callOffId,
    token,
  }: {
    callOffId: UUID | undefined;
    token: string | undefined;
  },
  options?: Omit<
    UseQueryOptions<CallOffPublicView, Error, CallOffPublicView>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(
    getCallOffViaMagicLinkQueryOptions<CallOffPublicView>({
      callOffId,
      token,
      options,
    }),
  );
